import {SerializedStyles} from '@emotion/react';
import {css, ClickAwayListener, DialogActions, InputAdornment, Popper, useTheme, Typography, useMediaQuery} from '@mui/material';
import {
  PickersActionBarProps,
  PickersLayoutProps,
  PickersLayoutRoot,
  PickersLayoutContentWrapper,
  pickersLayoutClasses,
  usePickerLayout,
  DateView,
  PickersDayProps,
  PickersDay,
} from '@mui/x-date-pickers';
import {DateUtils, DlMuiButton, DlMuiCalendarIcon, DlMuiDatePicker, DlMuiTextField, GetStylesFunction, utils} from '@wallaby-ui/core';
import classNames from 'classnames';
import {format, isAfter, isBefore, isValid} from 'date-fns';
import React, {useCallback, useEffect, useMemo, useRef, useState, MouseEvent} from 'react';
import {Placement} from 'react-bootstrap/esm/types';
import DatePicker from 'react-datepicker';
import {defineMessages, FormattedMessage, IntlShape, useIntl} from 'react-intl';

// import {FilterNode as FilterNodeModel} from 'src/js/model/filters_config';

import {STATIC_EMPTY_ARRAY} from './FilterNodeContainer';
import {FilterNodeRangeActionButtons} from './FilterNodeRangeActionButtons';
import {FilterNodeRangeShortcuts} from './FilterNodeRangeShortcuts';
import styles from './FilterNodeTypeDateRange.less';
import {FilterNodeTypeDateRangeCalendarHeader} from './FilterNodeTypeDateRangeCalendarHeader';
import {FilterSubmitButton} from './FilterSubmitButton';
import {ShortcutEnum, DateRangeFilter} from '../../../lib/filtering';
import {Shortcut} from '../../../lib/filtering/shortcut_types';
import {getShortcuts} from '../../../lib/filtering/shortcuts';

const I18N = defineMessages({
  FROM: {
    id: 'filter.date.from',
    defaultMessage: 'From',
    description: 'label for start date in date picker input',
  },
  TO: {
    id: 'filter.date.to',
    defaultMessage: 'To',
    description: 'label for end date in date picker input',
  },
  END_PLACEHOLDER: {
    id: 'filter.date.end.placeholder',
    defaultMessage: 'end date',
    description: 'placeholder for end date in date picker input',
  },
  START_PLACEHOLDER: {
    id: 'filter.date.start.placeholder',
    defaultMessage: 'start date',
    description: 'placeholder for start date in date picker input',
  },
  TODAY: {
    id: 'filter.date.today',
    defaultMessage: 'Today',
    description: 'text for today button in date picker input',
  },
  TOTAL_TEXT: {
    id: 'filter.date.totalText',
    defaultMessage: 'Total interval: {dateDifference} days',
    description: 'summary text for total interval',
  },
  MONTH_TOTAL_TEXT: {
    id: 'filter.month.totalText',
    defaultMessage: 'Total interval: {dateDifference} months',
    description: 'summary text for total interval',
  },
});

interface AdditionalProps {
  styles?: Record<string, SerializedStyles>;
  dateFrom?: boolean;
  intl?: IntlShape;
  dateDifference?: number;
  isSingleDate?: boolean;
  isMonthInput?: boolean;
}

export function DlMuiDatePickerLayout(
  props: PickersLayoutProps<Date | null, dateFns, DateView> & React.RefAttributes<HTMLDivElement>,
  additionalProps: AdditionalProps
) {
  const {toolbar, tabs, content, actionBar} = usePickerLayout(props);
  const {styles, dateFrom, intl, dateDifference, isSingleDate, isMonthInput} = additionalProps;
  return (
    <PickersLayoutRoot ownerState={props} css={styles?.pickersLayoutRootStyle}>
      {toolbar}
      {actionBar}
      <PickersLayoutContentWrapper className={pickersLayoutClasses.contentWrapper}>
        {dateFrom
          ? !isSingleDate && (
              <Typography css={styles?.dateTitle} variant={'sectionHeader'}>
                {intl && utils.TranslateUtils.translate('date.range.dateFrom', intl)}
              </Typography>
            )
          : !isSingleDate && (
              <Typography css={styles?.dateTitle} variant={'sectionHeader'}>
                {intl && utils.TranslateUtils.translate('date.range.dateTo', intl)}
              </Typography>
            )}
        {tabs}
        {content}
      </PickersLayoutContentWrapper>
    </PickersLayoutRoot>
  );
}

type DlMuiPickersDayProps = PickersDayProps<Date> & {
  startDate: Date | null;
  endDate: Date | null;
  isStartDate: boolean;
};

export function DlMuiPickersDay(props: DlMuiPickersDayProps) {
  const {day, startDate, endDate, isStartDate, ...other} = props;

  const theme = useTheme();

  if (startDate === null || endDate === null || !isValid(startDate) || !isValid(endDate)) {
    return <PickersDay day={day} {...other} />;
  }

  const dayIsBetween = isAfter(day, startDate) && isBefore(day, endDate);

  const formattedDay = format(day, DateUtils.ISO_DATE_ONLY_FORMAT);
  const formattedStartDate = format(startDate, DateUtils.ISO_DATE_ONLY_FORMAT);
  const formattedEndDate = format(endDate, DateUtils.ISO_DATE_ONLY_FORMAT);

  const isDayStartDate = formattedDay === formattedStartDate;
  const isDayEndDate = formattedDay === formattedEndDate;

  const muiStyles = dateRangeGetStyles({
    theme,
    params: {isDayStartDate, isDayEndDate},
  });

  let cssStyles;

  if (isDayStartDate && isStartDate) {
    cssStyles = muiStyles.startDay;
  } else if (isDayEndDate && !isStartDate) {
    cssStyles = muiStyles.endDay;
  } else if (isDayStartDate && !isStartDate) {
    cssStyles = muiStyles.selectedStartDay;
  } else if (isDayEndDate && isStartDate) {
    cssStyles = muiStyles.selectedEndDay;
  } else if (dayIsBetween) {
    cssStyles = muiStyles.dayBetween;
  }

  return <PickersDay {...other} day={day} css={cssStyles} />;
}

interface OwnProps {
  dateDifference: number;
  dateOnly?: boolean;
  handleButtonSubmit: (value?: [Date | null | undefined, Date | null | undefined, ShortcutEnum | null | undefined] | undefined) => void;
  handleChangeStart: (value: Date | null) => void;
  handleChangeEnd: (value: Date | null) => void;
  clearDates: () => void;
  resetToCommitted: () => void;
  inputRef: (input: HTMLElement) => void;
  isDisabled?: boolean;
  isEmpty?: boolean;
  isOpened?: boolean;
  isMui?: boolean;
  label?: string;
  startDate: Date | null;
  endDate: Date | null;
  shortcut?: ShortcutEnum | null;
  removeFilter: (filter: DateRangeFilter) => void;
  selectedFilter?: DateRangeFilter;
  isDefaultValue?: boolean;
  placement?: Placement;
  fullWidth?: boolean;
  enableFuture?: boolean;
  showShortcuts?: boolean;
  isLabResults?: boolean;
  isMedications?: boolean;
}

type Props = OwnProps;

export const FilterNodeTypeDateRange: React.FC<Props> = (props) => {
  const {
    dateDifference,
    handleButtonSubmit,
    handleChangeStart,
    handleChangeEnd,
    clearDates,
    resetToCommitted,
    inputRef,
    isDisabled = false,
    isEmpty = false,
    isMui = false,
    isOpened = false,
    label,
    startDate,
    endDate,
    shortcut,
    removeFilter,
    selectedFilter,
    isDefaultValue,
    placement = 'bottom-start',
    fullWidth = false,
    enableFuture = false,
    showShortcuts,
    isLabResults,
    isMedications = false,
  } = props;

  const theme = useTheme();
  const muiStyles = dateRangeGetStyles({theme, params: {fullWidth}});
  const intl = useIntl();
  const locale = utils.DateUtils.translateLocaleToDateFnsLocale(intl.locale);
  const dateFormat = utils.DateUtils.getLocaleDateFnsFormatPattern(intl.locale);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const isPopperOpened = Boolean(anchorEl);
  const [activeShortcutButton, setActiveShortcutButton] = useState<ShortcutEnum | null | undefined>(null);
  const isDailyView = true;

  // Discard uncommitted edits when the popper closes (ClickAway, Esc, or Apply
  // without a complete range), so abandoned From-only navigation does not bound
  // the next open's Date-to picker. Mirrors FilterNodeTypeDateMonthRange.
  const prevIsPopperOpenedRef = useRef(isPopperOpened);
  useEffect(() => {
    if (prevIsPopperOpenedRef.current && !isPopperOpened) {
      resetToCommitted();
    }
    prevIsPopperOpenedRef.current = isPopperOpened;
  }, [isPopperOpened, resetToCommitted]);

  const shortcuts = useMemo(
    () => getShortcuts(enableFuture, isDailyView, isLabResults, isMedications),
    [enableFuture, isDailyView, isLabResults, isMedications]
  );

  const handleShortcutSelect = useCallback(
    (dateRange: {start: Date; end: Date}, shortcut: ShortcutEnum | null) => {
      handleButtonSubmit([dateRange.start, dateRange.end, shortcut]);
    },
    [handleButtonSubmit]
  );

  const today = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return today;
  }, []);

  const normalizedDates = useMemo(() => {
    const minPickerDate = new Date(1900, 0, 1);
    const maxPickerDate = enableFuture ? new Date(2100, 0, 1) : today;

    const clampDate = (date: Date | null): Date | null => {
      if (!date) return null;
      if (date < minPickerDate) return minPickerDate;
      if (date > maxPickerDate) return maxPickerDate;
      return date;
    };

    return {
      startDate: clampDate(startDate),
      endDate: clampDate(endDate),
    };
  }, [startDate, endDate, enableFuture, today]);

  const clampToPickerBounds = useCallback(
    (date: Date): Date => {
      const minPickerDate = new Date(1900, 0, 1);
      const maxPickerDate = enableFuture ? new Date(2100, 0, 1) : today;

      if (date < minPickerDate) return minPickerDate;
      if (date > maxPickerDate) return maxPickerDate;
      return date;
    },
    [enableFuture, today]
  );

  const isSameDay = useCallback((a: Date | null, b: Date | null) => {
    if (!a || !b || !isValid(a) || !isValid(b)) return false;
    return format(a, DateUtils.ISO_DATE_ONLY_FORMAT) === format(b, DateUtils.ISO_DATE_ONLY_FORMAT);
  }, []);

  useEffect(() => {
    // Without a shortcut, we never auto-calculate anything.
    if (!shortcut) {
      // Only synchronize UI state (so the active shortcut button is correct)
      if (activeShortcutButton !== shortcut) setActiveShortcutButton(shortcut);
      return;
    }

    const matchedShortcut = Object.values(shortcuts)
      .flat()
      .find((sc: Shortcut) => sc.label === shortcut);

    if (!matchedShortcut) {
      if (activeShortcutButton !== shortcut) setActiveShortcutButton(shortcut);
      return;
    }

    // Expected date range based on the shortcut (clamped to picker bounds)
    const expectedRaw = matchedShortcut.getValue(); // { start: Date; end: Date }
    const expectedStart = clampToPickerBounds(expectedRaw.start);
    const expectedEnd = clampToPickerBounds(expectedRaw.end);

    const currentStart = normalizedDates.startDate;
    const currentEnd = normalizedDates.endDate;

    const missingDates = !currentStart || !currentEnd;
    const wrongDates = !missingDates && (!isSameDay(currentStart, expectedStart) || !isSameDay(currentEnd, expectedEnd));

    // If dates are missing or incorrect, recalculate and trigger onChange via handleShortcutSelect → handleButtonSubmit → ...
    if (missingDates || wrongDates) {
      setActiveShortcutButton(shortcut);
      handleShortcutSelect({start: expectedStart, end: expectedEnd}, shortcut);
      return;
    }

    if (activeShortcutButton !== shortcut) setActiveShortcutButton(shortcut);
  }, [
    shortcut,
    shortcuts,
    handleShortcutSelect,
    activeShortcutButton,
    normalizedDates.startDate,
    normalizedDates.endDate,
    clampToPickerBounds,
    isSameDay,
  ]);

  //TODO: PDH-2772
  /*const stripTimeFromDate = (date: Date | null) => {
    if (!date) return;
    return new Date(date?.getFullYear(), date?.getMonth(), date?.getDate());
  };

  const handleSelectMatchedShortcut = (newStartDate: Date | null, newEndDate: Date | null) => {
    const start = newStartDate ?? startDate;
    const end = newEndDate ?? endDate;

    const selectedStart = stripTimeFromDate(start)?.getTime();
    const selectedEnd = stripTimeFromDate(end)?.getTime();

    const matchedShortcut = [...shortcuts.dailyShortcuts, ...shortcuts.commonShortcuts].find((shortcut) => {
      const {start, end} = shortcut.getValue();

      return stripTimeFromDate(start)?.getTime() === selectedStart && stripTimeFromDate(end)?.getTime() === selectedEnd;
    });

    if (matchedShortcut) {
      handleButtonSubmit([matchedShortcut?.getValue().start, matchedShortcut?.getValue().end, matchedShortcut?.label]);
      setActiveShortcutButton(matchedShortcut.label ?? null);
    }
  };*/

  const mouseEventClick = useCallback(
    (event: MouseEvent<HTMLElement>) => {
      setAnchorEl(isPopperOpened ? null : event.currentTarget);
    },
    [isPopperOpened]
  );

  const handleDatePickerClose = () => {
    if (isPopperOpened) {
      setAnchorEl(null);
    }
  };

  const handleDatePickerClear = () => {
    if (isPopperOpened) {
      setAnchorEl(null);
    }
    if (selectedFilter) {
      removeFilter(selectedFilter);
    }
    clearDates();
    setActiveShortcutButton(null);
  };

  const formattedDateRange = useMemo(() => {
    const formattedStart = utils.DateUtils.getFormattedDate(normalizedDates.startDate, intl, true, true);
    const formattedEnd = utils.DateUtils.getFormattedDate(normalizedDates.endDate, intl, true, true);
    return isEmpty ? '' : `${formattedStart} - ${formattedEnd}`;
  }, [normalizedDates.startDate, normalizedDates.endDate, intl, isEmpty]);

  const mediaMatches = useMediaQuery('(min-height:800px)');

  return isMui ? (
    <>
      <div css={muiStyles.textFieldWrapper}>
        <DlMuiTextField
          data-name={'filter-node-type-date-range--input'}
          label={label ?? ''}
          variant={'outlined'}
          onClick={mouseEventClick}
          InputProps={{
            readOnly: true,
            endAdornment: (
              <InputAdornment position='end'>
                <DlMuiCalendarIcon />
              </InputAdornment>
            ),
          }}
          value={formattedDateRange}
          inputCss={muiStyles.textField}
          isDefaultValue={isDefaultValue}
        />
      </div>
      {isPopperOpened ? (
        <ClickAwayListener
          mouseEvent='onMouseDown'
          onClickAway={(event) => {
            if (anchorEl && anchorEl.contains(event.target as HTMLElement)) {
              return;
            }
            handleDatePickerClose();
          }}
        >
          <Popper
            data-name={'dl-mui-popper--popper'}
            open={isPopperOpened}
            anchorEl={anchorEl}
            placement={mediaMatches ? placement : 'auto-start'}
            popperOptions={{
              modifiers: [
                {
                  name: 'preventOverflow',
                  options: {
                    boundary: document.querySelector('[data-name="content-column"]'),
                    padding: {right: 16},
                  },
                },
              ],
            }}
            css={muiStyles.popper}
            disablePortal
          >
            <div
              css={muiStyles.dateRangeContainer}
              className={classNames({
                dateRangeContainerColumn: showShortcuts,
              })}
            >
              <div css={muiStyles.dateRangePickerWrapper}>
                {/* TODO: check this for displaying range - https://mui.com/x/react-date-pickers/date-calendar/#custom-day-rendering */}
                <DlMuiDatePicker
                  data-name={'filter-node-type-date-range--start'}
                  slotProps={{
                    actionBar: {hidden: true},
                  }}
                  slots={{
                    layout: (layoutProps) =>
                      DlMuiDatePickerLayout(layoutProps, {
                        styles: muiStyles,
                        dateFrom: true,
                        intl,
                        dateDifference,
                      }),
                    day: (dayProps) =>
                      DlMuiPickersDay({
                        ...dayProps,
                        endDate: normalizedDates.endDate,
                        startDate: normalizedDates.startDate,
                        isStartDate: true,
                      }),
                    calendarHeader: (headerProps) => (
                      <FilterNodeTypeDateRangeCalendarHeader
                        enableFuture={enableFuture}
                        {...headerProps}
                        boundMaxDate={normalizedDates.endDate}
                      />
                    ),
                  }}
                  value={normalizedDates.startDate}
                  // TODO: set endDate for startDate if it is lower
                  onChange={(value) => {
                    handleChangeStart(value);
                    handleButtonSubmit([value, undefined, undefined]);
                    // handleSelectMatchedShortcut(value, null);
                  }}
                  maxDate={normalizedDates.endDate ?? (enableFuture ? undefined : today)}
                />
                <DlMuiDatePicker
                  data-name={'filter-node-type-date-range--end'}
                  value={normalizedDates.endDate}
                  minDate={normalizedDates.startDate ?? undefined}
                  maxDate={enableFuture ? undefined : today}
                  onChange={(value) => {
                    handleChangeEnd(value);
                    handleButtonSubmit([undefined, value, undefined]);
                    // handleSelectMatchedShortcut(null, value);
                  }}
                  slotProps={{
                    actionBar: {hidden: true},
                  }}
                  slots={{
                    layout: (customLayoutProps) =>
                      DlMuiDatePickerLayout(customLayoutProps, {
                        styles: muiStyles,
                        dateFrom: false,
                        intl,
                      }),
                    day: (dayProps) =>
                      DlMuiPickersDay({
                        ...dayProps,
                        endDate: normalizedDates.endDate,
                        startDate: normalizedDates.startDate,
                        isStartDate: false,
                      }),
                    calendarHeader: (headerProps) => (
                      <FilterNodeTypeDateRangeCalendarHeader
                        enableFuture={enableFuture}
                        {...headerProps}
                        boundMinDate={normalizedDates.startDate}
                      />
                    ),
                  }}
                />
              </div>
              <div
                css={muiStyles.barWrapper}
                className={classNames({
                  barWrapperColumn: showShortcuts,
                })}
              >
                {showShortcuts === true && (
                  <FilterNodeRangeShortcuts
                    isDailyView={isDailyView}
                    onShortcutSelect={handleShortcutSelect}
                    activeShortcutButton={activeShortcutButton ?? null}
                    setActiveShortcutButton={setActiveShortcutButton}
                    shortcuts={shortcuts}
                  />
                )}
                <FilterNodeRangeActionButtons onDatePickerClear={handleDatePickerClear} onDatePickerClose={handleDatePickerClose} />
              </div>
            </div>
          </Popper>
        </ClickAwayListener>
      ) : null}
    </>
  ) : (
    <>
      <div className={classNames('dlFilterNodeContentInput', styles.dlFilterNodeTypeDateRangeInputWrapper)}>
        <span>
          <FormattedMessage {...I18N.FROM} />
        </span>
        <div className={styles.dlInnerDataPickerWrapper} data-name={'dateFrom'}>
          <DatePicker
            className={styles.dlFilterNodeTypeDateRangeInput}
            onChange={handleChangeStart}
            dateFormat={dateFormat}
            defaultValue={STATIC_EMPTY_ARRAY}
            disabledKeyboardNavigation
            dropdownMode={'select'}
            locale={locale}
            peekNextMonth
            placeholderText={utils.TranslateUtils.translate(I18N.START_PLACEHOLDER, intl)}
            ref={(input) => inputRef(input ? input.input : null)}
            selected={normalizedDates.startDate}
            selectsEnd
            selectsStart
            scrollableYearDropdown
            showYearDropdown
            showMonthDropdown
            startDate={normalizedDates.startDate}
            endDate={normalizedDates.endDate}
            tabIndex={isOpened ? 0 : -1}
            todayButton={utils.TranslateUtils.translate(I18N.TODAY, intl)}
            utc
            minDate={new Date(1900, 0, 1)}
            maxDate={enableFuture ? undefined : today}
          />
        </div>
        <i className={classNames('far fa-calendar-alt', styles.dlFilterNodeTypeDateRangeIcon)} />
        <span>
          <FormattedMessage {...I18N.TO} />
        </span>
        <div className={styles.dlInnerDataPickerWrapper} data-name={'dateTo'}>
          <DatePicker
            className={styles.dlFilterNodeTypeDateRangeInput}
            onChange={handleChangeEnd}
            dateFormat={dateFormat}
            defaultValue={STATIC_EMPTY_ARRAY}
            disabledKeyboardNavigation
            dropdownMode={'select'}
            locale={locale}
            peekNextMonth
            placeholderText={utils.TranslateUtils.translate(I18N.END_PLACEHOLDER, intl)}
            selected={normalizedDates.endDate}
            selectsEnd
            selectsStart
            scrollableYearDropdown
            showYearDropdown
            showMonthDropdown
            startDate={normalizedDates.startDate}
            endDate={normalizedDates.endDate}
            tabIndex={isOpened ? 0 : -1}
            todayButton={utils.TranslateUtils.translate(I18N.TODAY, intl)}
            utc
            minDate={normalizedDates.startDate ?? new Date(1900, 0, 1)}
            maxDate={enableFuture ? undefined : today}
          />
        </div>
        <i className={classNames('far fa-calendar-alt', styles.dlFilterNodeTypeDateRangeIcon)} />
      </div>
      <div className={styles.dlDayDifferenceWrapper}>
        <span
          className={classNames({
            [styles.dlDayDifferenceBad]: dateDifference < 0,
            [styles.dlDayDifferenceGood]: dateDifference >= 0,
          })}
        >
          <FormattedMessage {...I18N.TOTAL_TEXT} values={{dateDifference}} />
        </span>
        <FilterSubmitButton onClick={handleButtonSubmit} isDisabled={isDisabled} tabIndex={isOpened ? 0 : -1} />
      </div>
    </>
  );
};

FilterNodeTypeDateRange.displayName = 'FilterNodeTypeDateRange';

export const dateRangeGetStyles: GetStylesFunction = (options) => {
  const isDayStartDate = options?.params?.isDayStartDate;
  const isDayEndDate = options?.params?.isDayEndDate;
  const fullWidth = options?.params?.fullWidth;
  return {
    singleDateFilterWrapper: css`
      max-width: 362px;
    `,
    textFieldWrapper: css`
      display: flex;
      height: ${options?.params?.hasError ? 62 : 53}px;
      width: 100%;
      max-width: ${fullWidth ? '100%' : '176px'};
      overflow: hidden;
    `,
    textField: css({
      'height': '38px',
      'width': '100%',
      'margin': '8px 0 4px 0',
      'cursor': 'pointer',
      '& .MuiInputBase-root': {
        cursor: 'pointer',
      },
      '& .MuiInputBase-input': {
        cursor: 'pointer',
      },
    }),
    popper: css`
      z-index: 1300 !important;
      width: fit-content;
      background-color: ${options?.theme?.palette.common.white};
      padding: ${options?.theme?.spacing(3)};
      box-shadow: 0 0 12px 0 #12161914;
    `,
    dateRangeContainer: css`
      display: flex;
      flex-direction: column;

      &.dateRangeContainerColumn {
        flex-direction: row;
      }
    `,
    dateRangePickerWrapper: css`
      display: flex;
      flex-direction: row;
    `,
    barWrapper: css`
      display: flex;
      justify-content: flex-end;
      padding-right: ${options?.theme?.spacing(10)};

      &.barWrapperColumn {
        flex-direction: column;
        justify-content: space-between;
        align-items: center;
        padding-right: 0;
      }
    `,
    filledButton: css`
      background-color: var(--dlTheme-500);
      color: white;

      &:hover,
      &:focus {
        background-color: var(--dlTheme-600);
        color: white;
      }

      &:active {
        background-color: var(--dlTheme-700);
        color: white;
      }
    `,
    pickersLayoutRootStyle: css({
      'width': '340px',
      '& .MuiPickersLayout-contentWrapper': {
        '& .MuiPickersDay-root.Mui-selected': {
          backgroundColor: 'var(--dlTheme-500)',
        },
        '& .MuiPickersMonth-monthButton.Mui-selected': {
          backgroundColor: 'var(--dlTheme-500)',
        },
        '& .MuiPickersYear-yearButton.Mui-selected': {
          backgroundColor: 'var(--dlTheme-500)',
        },
        '& .MuiPickersDay-root.MuiPickersDay-today:not(.Mui-selected)': {
          border: `1px solid var(--dlTheme-500)`,
        },
        '& .MuiPickersDay-root': {
          margin: 0,
        },
      },
    }),
    dateTitle: css`
      color: var(--dlThemeGray-900);
      grid-column: 2;
      grid-row: 1;
      text-align: center;
    `,
    dayBetween: css({
      backgroundColor: 'var(--dlTheme-50)',
      borderRadius: 0,
    }),
    startDay: css({
      backgroundColor: 'var(--dlTheme-700)',
      borderTopRightRadius: isDayEndDate === isDayStartDate ? '50%' : 0,
      borderBottomRightRadius: isDayEndDate === isDayStartDate ? '50%' : 0,
    }),
    endDay: css({
      backgroundColor: 'var(--dlTheme-700)',
      borderTopLeftRadius: isDayEndDate === isDayStartDate ? '50%' : 0,
      borderBottomLeftRadius: isDayEndDate === isDayStartDate ? '50%' : 0,
    }),
    selectedEndDay: css({
      backgroundColor: 'var(--dlTheme-100)',
      borderTopLeftRadius: isDayEndDate === isDayStartDate ? '50%' : 0,
      borderBottomLeftRadius: isDayEndDate === isDayStartDate ? '50%' : 0,
    }),
    selectedStartDay: css({
      backgroundColor: 'var(--dlTheme-100)',
      borderTopRightRadius: isDayEndDate === isDayStartDate ? '50%' : 0,
      borderBottomRightRadius: isDayEndDate === isDayStartDate ? '50%' : 0,
    }),
    selectedDateRange: css`
      color: var(--dlTheme-500);
      height: 42px;
      grid-column: 2;
      grid-row: 3;
      display: flex;
      justify-content: center;
      align-items: center;
    `,
    monthPicker: css`
      .MuiPickersMonth-root[data-in-range='true'] .MuiPickersMonth-monthButton,
      .MuiPickersMonth-root[data-range-start='true'] .MuiPickersMonth-monthButton,
      .MuiPickersMonth-root[data-range-end='true'] .MuiPickersMonth-monthButton {
        background-color: transparent;
      }

      .MuiPickersMonth-root[data-in-range='true'] .MuiPickersMonth-monthButton:hover,
      .MuiPickersMonth-root[data-range-start='true'] .MuiPickersMonth-monthButton:hover,
      .MuiPickersMonth-root[data-range-end='true'] .MuiPickersMonth-monthButton:hover {
        background-color: transparent;
      }

      .MuiPickersMonth-root[data-in-range='true'] {
        position: relative;

        &::before {
          content: '';
          position: absolute;
          top: 8px;
          bottom: 8px;
          left: 0;
          right: 0;
          background-color: var(--dlTheme-50);
          z-index: -1;
        }
      }

      .MuiPickersMonth-root[data-range-start='true'] {
        position: relative;

        &::before {
          content: '';
          position: absolute;
          top: 8px;
          bottom: 8px;
          left: 0;
          right: 0;
          background-color: var(--dlTheme-500);
          border-radius: 8px 0 0 8px;
          z-index: -1;
        }

        .MuiPickersMonth-monthButton {
          color: white !important;
        }
      }

      .MuiPickersMonth-root[data-range-end='true'] {
        position: relative;

        &::before {
          content: '';
          position: absolute;
          top: 8px;
          bottom: 8px;
          left: 0;
          right: 0;
          background-color: var(--dlTheme-500);
          border-radius: 0 8px 8px 0;
          z-index: -1;
        }

        .MuiPickersMonth-monthButton {
          color: white !important;
        }
      }

      .MuiPickersMonth-root[data-range-start='true'][data-range-end='true'] {
        &::before {
          border-radius: 8px;
        }
      }

      .MuiPickersMonth-root[data-year-end='true'] {
        &::before {
          border-radius: 0 8px 8px 0;
        }
      }

      .MuiPickersMonth-root[data-year-start='true'] {
        &::before {
          border-radius: 8px 0 0 8px;
        }
      }
    `,
  };
};
